/*
    シミュレーション画面ナビゲーションリンク生成コンポーネント
 */

import { ButtonParts } from "~/components/atoms/ButtonParts"
import { CheckBox } from "~/components/atoms/CheckBox"
import { Link } from "~/utils/Link"
import React, { useMemo, useState } from "react"
import { useRouter } from "next/router"
import { PAGES } from "~/data/constants/Page"
import { useDispatch, useSelector } from "react-redux"
import { allReset, selectCustomer } from "~/slices/customerSlice"
import { selectPage } from "~/slices/pageSlice"
import { Api, EstimateType } from "~/api/Api"
import { MailType } from "~/models/Mail"
import { ModalContent } from "~/models/Modal"
import { ContractItemCatalogConnect, NewTenantContractApplyContent } from "~/models/ContractItem";
import { PageKey, SimulatorMode } from "~/models/Page";
import { PlanType } from "~/models/Plan"
import { Calculation } from "~/utils/Calculation";
import { selectProduct } from "~/slices/productSlice";
import { Modal } from "react-bootstrap";
import { CONTRACT_SERVICE_CLASS_ID } from "~/data/constants/ContractServiceClassId"

interface Props {
  action?: (type: string) => void,
}

export function SimpleNavigationLink(props: Props) {
  const { action } = props
  const { query } = useRouter()
  const router = useRouter()
  const dispatch = useDispatch()
  const storeIndex = useSelector(selectPage)?.selectStoreIndex
  const customerData = useSelector(selectCustomer)
  const basicPack = (customerData.stores[storeIndex].selectData || {})[PageKey.BasicPack]
  const productData = useSelector(selectProduct)
  const allStoreSummaries = Calculation.summaries(productData, customerData)
  const [dialog, dialogState] = useState<ModalContent>({
    status: false,
    content: { header: "", body: "", footer: "", type: "success" }
  })
  const [isChecked, setIsChecked] = useState(false);
  const disabled = useMemo(() => !customerData.stores[storeIndex].isValid, [customerData.stores[storeIndex].isValid])

  // 新規テナント契約申込内容
  const newTenantContractApplyContentList: NewTenantContractApplyContent[] = []

  // 合計金額画面なら 新規テナント契約申込内容を生成
  if (query.page === PageKey.SimpleEstimate) {
    const monthlyConnectList = [];              // 月額契約
    const automotiveConnectList = [];           // 整鈑車長期契約
    const electronicDocumentConnectList = [];   // 電帳長期契約

    const targets: PageKey[] = [PageKey.BasicPack, PageKey.AddPack, PageKey.Option, PageKey.Database, PageKey.MaService, PageKey.InstSup]
    allStoreSummaries.forEach(summaries => {
      summaries.subtotals.filter(item => targets.includes(item.key as PageKey)).forEach(item => {
        (item.items || []).filter(item => item && !/^_/.test(item.id)).forEach(product => {
          let contractServiceClassId = "";
          const contractServiceClassIdList = product.contractServiceClassIdList || [];
          if (contractServiceClassIdList.length === 1) {
            contractServiceClassId = product.contractServiceClassIdList[0];
          } else {
            contractServiceClassId = contractServiceClassIdList.find(contractServiceClassId => {
              // 料金シミュレータで契約可能なのは整板車か電帳なのでどちらかを設定
              // NOTE: 2022/10/25時点では整備・鈑金・車販と電帳の両方で契約できる商品は無いのでこの仕様だがあやうい感じ...
              return contractServiceClassId === CONTRACT_SERVICE_CLASS_ID.SF_REPAIR_CARSALES
                || contractServiceClassId === CONTRACT_SERVICE_CLASS_ID.ELECTRONIC_DOCUMENT
            });
          }

          // 契約商品カタログ紐付
          const contractItemCatalogConnect: ContractItemCatalogConnect = {
            providerTenantId: product.providerTenantId,
            itemCatalogId: product.id,
            contractQuantity: product.contract || 1,
            contractServiceClassId,
          }

          if (basicPack.planType === PlanType.Month) {
            // 月額契約を選択時は全て月額契約リストに入れる
            monthlyConnectList.push(contractItemCatalogConnect)
          } else {
            // 長期契約を選択時
            if ((product.price.amounts[basicPack.planType] || 0) === 0) {
              // 長期契約金額を持ってない商品は月額契約固定
              monthlyConnectList.push(contractItemCatalogConnect)
            } else {
              // 長期契約金額を持ってる場合は契約サービス分類別に格納
              if (contractServiceClassId === CONTRACT_SERVICE_CLASS_ID.SF_REPAIR_CARSALES) {
                automotiveConnectList.push(contractItemCatalogConnect)
              } else if (contractServiceClassId === CONTRACT_SERVICE_CLASS_ID.ELECTRONIC_DOCUMENT) {
                electronicDocumentConnectList.push(contractItemCatalogConnect)
              }
            }
          }
        })
      })
    });

    if (monthlyConnectList.length > 0) {
      newTenantContractApplyContentList.push({
        contractServiceClassId: CONTRACT_SERVICE_CLASS_ID.MONTHLY_CONTRACT,
        typeContractDiv: PlanType.Month,
        contractItemCatalogConnectList: monthlyConnectList
      })
    }

    if (automotiveConnectList.length > 0) {
      newTenantContractApplyContentList.push({
        contractServiceClassId: CONTRACT_SERVICE_CLASS_ID.SF_REPAIR_CARSALES,
        typeContractDiv: basicPack.planType,
        contractItemCatalogConnectList: automotiveConnectList
      })
    }

    if (electronicDocumentConnectList.length > 0) {
      newTenantContractApplyContentList.push({
        contractServiceClassId: CONTRACT_SERVICE_CLASS_ID.ELECTRONIC_DOCUMENT,
        typeContractDiv: basicPack.planType,
        contractItemCatalogConnectList: electronicDocumentConnectList
      })
    }
  }

  const onApplySend = async () => {
    try {
      const applyContractResult = await Api.applyContract(customerData.estimateId, newTenantContractApplyContentList)
      await Api.putItemEstimateAPI(customerData.estimateId, customerData.accessTokenId, customerData, EstimateType.COMMIT)
      dispatch(allReset())
      location.href = `/contract/account/account-creation-page?contractApplyToken=${applyContractResult}`
    } catch (error) {
      console.error(error)
      const errorMessages = {
        applyContract: (error.response.data.messages || []).join("　"),
        putItemEstimateAPI: "問題が発生しました。料金シミュレートを最初からやり直してください。"
      }
      dialogState({
        status: true,
        content: { header: "エラー", body: errorMessages[error.api], footer: "閉じる", type: "error" }
      })

    }
  }

  const onMailSend = async () => {
    try {
      await Api.putItemEstimateAPI(customerData.estimateId, customerData.accessTokenId, customerData, EstimateType.SAVE)

      const result = await Api.sendMail({
        estimateId: customerData.estimateId,
        accessTokenId: customerData.accessTokenId,
        mailType: MailType.estimate,
        email: customerData.company.email,
        name: customerData.company.name,
        picName: customerData.company.user,
        tel: customerData.company.tel,
        pageType: SimulatorMode.Simplified
      })

      if (result) {
        dialogState({
          status: true,
          content: {
            header: "見積り内容を送信しました。",
            body: "ご登録頂いたメールアドレス宛に、見積りの内容のURLを記載したメールをお送りしました。お申し込みの場合は閉じるボタンで操作を進めてください。",
            footer: "閉じる",
            type: "success"
          }
        })
      }
    } catch (e) {
      console.error(e.response)
      dialogState({
        status: true,
        content: {
          header: "エラー",
          body: "問題が発生しました。料金シミュレートを最初からやり直してください。",
          footer: "閉じる",
          type: "error"
        },
        url: "/simulator/"
      })
    }
  }

  const onHide = () => {
    if (dialog.url) {
      router.push(dialog.url)
    }
    dialogState({ ...dialog, status: false })
  }
  return (
    <>
      {query.page === PageKey.SimpleEstimate ?
        <>
          <div className="pack-info-check-area">
            <CheckBox
              name={"confirm"} value={{
              label: "従量料金が発生することを理解しました。",
              checked: isChecked,
            }} onChange={() => {
              setIsChecked(!isChecked)
            }}
            />
          </div>
        </>
        :
        <></>
      }
      <div className="pack-info-btn-area">
        <ButtonParts
          className={"btn btn-light"}
          url={Link.simulator.prev(query, customerData.company)}
          action={action.bind(this, "prev")}
        >
          <span className="ar-left"></span>
          戻る
        </ButtonParts>
        {query.page === PageKey.SimpleEstimate ?
          <>
            <ButtonParts
              className={"btn btn-light"}
              action={onMailSend}
            >
              見積りをメールで送信
              <span className="ar-right"></span>
            </ButtonParts>
            <ButtonParts
              className={"btn btn-primary"}
              action={onApplySend}
              disabled={!isChecked}
            >
              申し込む
              <span className="ar-right"></span>
            </ButtonParts>
          </>
          :
          <ButtonParts
            disabled={disabled}
            className={"btn btn-primary"}
            url={Link.simulator.next(query, customerData.company)}
            action={action.bind(this, "next")}
          >
            {PAGES[Link.simulator.nextPage(query, customerData.company)].label}へ
            <span className="ar-right"></span>
          </ButtonParts>
        }

      </div>

      <div className="sp-btn-area">
        <ButtonParts
          className={"btn btn-light"}
          url={Link.simulator.prev(query, customerData.company)}
          action={action.bind(this, "prev")}
        >
          <span className="ar-left"></span>
          戻る
        </ButtonParts>
        {query.page !== PageKey.SimpleEstimate && (<ButtonParts
          disabled={query.page === PageKey.SimpleEstimate}
          className={"btn btn-light"}
          url={Link.simulator.next(query, customerData.company)}
          action={action.bind(this, "next")}
        >
          次へ
          <span className="ar-right"></span>
        </ButtonParts>)}
      </div>
      <Modal id={"error-modal"} show={dialog.status} onHide={onHide}>
        <Modal.Header>
          {dialog.content.type === "error" && <span className="error-icon"></span>}
          <div className="modal-title" id="modal-title">{dialog.content.header}</div>
          <ButtonParts className={"btn close"} action={onHide.bind(this, true)}>
            <span aria-hidden="true">&times;</span>
          </ButtonParts>
        </Modal.Header>
        <Modal.Body>
          <p>{dialog.content.body}</p>
        </Modal.Body>
        <Modal.Footer>
          <ButtonParts className={"btn btn-light"} action={onHide.bind(this, true)}>閉じる<span
            className="ar-right"></span></ButtonParts>
        </Modal.Footer>
      </Modal>
    </>
  )
}
